import React, { useState, useEffect, useRef } from "react";
import {
  CameraIcon,
  FilmIcon,
  MegaphoneIcon,
  PhotoIcon,
  RocketLaunchIcon,
  SparklesIcon,
  Squares2X2Icon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  TagIcon,
  TicketIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import AnanasSolutionsLogo from "../images/AnanasSolutionsLogo.webp";
import Button from "../components/controls/Button";
import { useTranslation } from "react-i18next";

/* ====== PALETTE CENTRALISÉE ====== */
const BRAND = {
  primary: "from-[#FDB813] to-[#4CAF50]", // Jaune ananas -> Vert feuille
  primarySoft: "from-[#FFE9A6] to-[#B7E3B4]",
  accent: "from-[#C85A2C] to-[#B35C38]", // Papaya Title/Text (si tu veux un 2e ton)
};

export default function AboutUs() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState({});
  const [counts, setCounts] = useState({ clients: 0, projects: 0, years: 0 });
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll("[data-animate]").forEach((el) => {
      if (observerRef.current) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    const animateCount = (target, key) => {
      let current = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCounts((prev) => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setCounts((prev) => ({ ...prev, [key]: Math.floor(current) }));
        }
      }, 30);
    };

    if (isVisible["stats"]) {
      animateCount(50, "clients");
      animateCount(200, "projects");
      animateCount(5, "years");
    }
  }, [isVisible]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-emerald-50 via-white to-yellow relative overflow-hidden">
      {/* HERO */}
      <section className="relative mx-auto w-full pt-20 pb-14 bg-gradient-to-br from-[#E9FBFA] via-[#FFF9E6] to-[#FFE9EC] overflow-hidden">
        <div className="absolute inset-0 opacity-20 blur-[1px]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${AnanasSolutionsLogo})`,
              backgroundRepeat: "repeat",
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        <div className="relative flex flex-col items-center text-center px-4">
          <div className="transform transition-all duration-700 hover:scale-105">
            <div
              className={`w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 xl:w-96 xl:h-96 rounded-full bg-gradient-to-br ${BRAND.primary} animate-pulse-slow shadow-2xl flex items-center justify-center`}
            >
              <div className="w-44 h-44 sm:w-52 sm:h-52 md:w-60 md:h-60 lg:w-[19rem] lg:h-[19rem] xl:w-[22rem] xl:h-[22rem] rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg p-8">
                <img
                  src={AnanasSolutionsLogo}
                  alt={t("about.hero.logoAlt")}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>

          <h1 className="mt-10 mb-2 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-loose text-[#3a3a3a] drop-shadow-sm font-kaushan px-8 py-6 overflow-visible">
            <span className="inline-block animate-fade-in-up pr-2">
              {t("about.hero.title.connecter")}
            </span>{" "}
            <span className="inline-block animate-fade-in-up animation-delay-200 pr-2">
              {t("about.hero.title.inspirer")}
            </span>{" "}
            <span
              className={`inline-block animate-fade-in-up animation-delay-400 bg-gradient-to-r ${BRAND.primary} bg-clip-text text-transparent pr-4`}
            >
              {t("about.hero.title.propulser")}
            </span>
          </h1>

          <p className="mt-6 max-w-3xl text-base sm:text-lg md:text-xl text-zinc-700 animate-fade-in-up animation-delay-600">
            {t("about.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* QUI NOUS SOMMES */}
      <section
        id="qui-nous-sommes"
        data-animate
        className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-12 transition-all duration-1000 ${
          isVisible["qui-nous-sommes"]
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="group rounded-3xl bg-white/80 backdrop-blur-xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-rose-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              {/* ✅ Barre avec couleurs ananas */}
              <div
                className={`h-1 w-12 bg-gradient-to-r ${BRAND.primary} rounded-full`}
              ></div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">
                {t("about.whoWeAre.title")}
              </h2>
            </div>
            <p className="max-w-4xl text-lg text-zinc-700 leading-relaxed">
              {t("about.whoWeAre.paragraph")}
            </p>
          </div>
        </div>
      </section>

      {/* CE QUE NOUS FAISONS / SERVICES */}
      <section
        id="services"
        className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-12"
      >
        {/* Publicités & médias */}
        <CardSection
          id="pub-medias"
          title={t("about.services.media.title")}
          isVisible={isVisible["pub-medias"]}
          badgeGradient={BRAND.primary}
        >
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <ServiceItem
              icon={CameraIcon}
              title={t("about.services.media.items.photography.title")}
              desc={t("about.services.media.items.photography.desc")}
              badge={t("about.badges.popular")}
            />
            <ServiceItem
              icon={FilmIcon}
              title={t("about.services.media.items.videosInterviews.title")}
              desc={t("about.services.media.items.videosInterviews.desc")}
            />
            <ServiceItem
              icon={RocketLaunchIcon}
              title={t("about.services.media.items.drones.title")}
              desc={t("about.services.media.items.drones.desc")}
              badge={t("about.badges.wow")}
            />
            <ServiceItem
              icon={SparklesIcon}
              title={t("about.services.media.items.videoEditing.title")}
              desc={t("about.services.media.items.videoEditing.desc")}
            />
            <ServiceItem
              icon={Squares2X2Icon}
              title={t("about.services.media.items.fbIgAds.title")}
              desc={t("about.services.media.items.fbIgAds.desc")}
            />
            <ServiceItem
              icon={ChatBubbleLeftRightIcon}
              title={t("about.services.media.items.strategy.title")}
              desc={t("about.services.media.items.strategy.desc")}
            />
          </div>
        </CardSection>

        {/* Design & infographie */}
        <CardSection
          id="design"
          title={t("about.services.design.title")}
          icon={<PencilSquareIcon className="h-7 w-7" />}
          className="mt-10"
          isVisible={isVisible["design"]}
          badgeGradient={BRAND.primary}
        >
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <ServiceItem
              icon={PhotoIcon}
              title={t("about.services.design.items.postersBanners.title")}
              desc={t("about.services.design.items.postersBanners.desc")}
            />
            <ServiceItem
              icon={TagIcon}
              title={t("about.services.design.items.logoIdentity.title")}
              desc={t("about.services.design.items.logoIdentity.desc")}
              badge={t("about.badges.new")}
            />
            <ServiceItem
              icon={Squares2X2Icon}
              title={t("about.services.design.items.customMenus.title")}
              desc={t("about.services.design.items.customMenus.desc")}
            />
            <ServiceItem
              icon={TicketIcon}
              title={t(
                "about.services.design.items.businessCardsVisuals.title"
              )}
              desc={t("about.services.design.items.businessCardsVisuals.desc")}
            />
            <ServiceItem
              icon={TagIcon}
              title={t("about.services.design.items.labelsPackaging.title")}
              desc={t("about.services.design.items.labelsPackaging.desc")}
            />
          </div>
        </CardSection>
      </section>

      {/* RAYONNEMENT */}
      <section
        id="rayonnement"
        data-animate
        className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-12 transition-all duration-1000 ${
          isVisible["rayonnement"]
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="relative rounded-3xl bg-gradient-to-br from-amber-50 via-white to-rose-50 p-8 md:p-10 shadow-2xl border-2 border-amber-200/50 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-200/30 to-rose-200/30 rounded-full blur-3xl"></div>

          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${BRAND.primary} rounded-2xl blur-lg opacity-50 animate-rotate`}
                ></div>
                <IconBadge color={BRAND.primary}>
                  <MegaphoneIcon className="h-6 w-6 text-white" />
                </IconBadge>
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-zinc-900">
                  {t("about.promoProgram.title")}
                </h3>
                <p className="text-sm text-zinc-600 mt-1">
                  {t("about.promoProgram.subtitle")}
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.visibility.title")}
                desc={t("about.promoProgram.benefits.visibility.desc")}
              />
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.storeTickets.title")}
                desc={t("about.promoProgram.benefits.storeTickets.desc")}
              />
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.proContent.title")}
                desc={t("about.promoProgram.benefits.proContent.desc")}
              />
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.strategy.title")}
                desc={t("about.promoProgram.benefits.strategy.desc")}
              />
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.customEditing.title")}
                desc={t("about.promoProgram.benefits.customEditing.desc")}
              />
              <BenefitCard
                icon={CheckCircleIcon}
                title={t("about.promoProgram.benefits.extras.title")}
                desc={t("about.promoProgram.benefits.extras.desc")}
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <Button
                as="a"
                href="/devenir-partenaire"
                color="primary"
                size="lg"
                className="group w-full sm:w-auto shadow-lg hover:shadow-xl"
                rightIcon={
                  <SparklesIcon className="h-5 w-5 transition-transform duration-500 group-hover:rotate-180" />
                }
              >
                {t("about.promoProgram.cta.choosePlan")}
              </Button>
              <a
                href="/contact"
                className="group flex items-center gap-2 rounded-full px-7 py-3.5 bg-white/80 backdrop-blur text-zinc-900 hover:bg-white transition-all shadow-md hover:shadow-lg font-semibold border border-zinc-200"
              >
                {t("about.promoProgram.cta.talkTeam")}
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section
        id="pourquoi"
        data-animate
        className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-20 transition-all duration-1000 ${
          isVisible["pourquoi"]
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="group rounded-3xl bg-white/80 backdrop-blur-xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`h-1 w-12 bg-gradient-to-r ${BRAND.primary} rounded-full`}
              ></div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">
                {t("about.why.title")}
              </h2>
            </div>
            <p className="max-w-4xl text-lg text-zinc-700 leading-relaxed">
              {t("about.why.paragraph")}
            </p>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes blob {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
          opacity: 0;
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/* ---------- Sous-composants ---------- */

function StatCard({ value, label, color = BRAND.primary, delay }) {
  const { t } = useTranslation();
  return (
    <div
      className="group rounded-2xl bg-white/80 backdrop-blur-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 hover:scale-105"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${color} mb-3 shadow-lg`}
      >
        <SparklesIcon className="h-6 w-6 text-white" />
      </div>
      <div
        className={`text-4xl font-bold bg-gradient-to-r ${color} bg-clip-text text-transparent mb-2`}
      >
        {value}
      </div>
      <div className="text-sm font-medium text-zinc-600">{label}</div>
    </div>
  );
}

function CardSection({
  id,
  title,
  icon,
  children,
  className = "",
  isVisible,
  badgeGradient = BRAND.primary,
}) {
  return (
    <div
      id={id}
      data-animate
      className={`rounded-3xl bg-white/80 backdrop-blur-xl p-8 md:p-10 shadow-xl border border-white/20 transition-all duration-1000 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      } ${className}`}
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="relative"></div>
        <h3 className="text-2xl md:text-3xl font-bold text-zinc-900">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

/* → Icônes unifiées : color par défaut = BRAND.primary */
function ServiceItem({
  icon: Icon,
  title,
  desc,
  color = BRAND.primary,
  badge,
}) {
  return (
    <div className="group relative">
      {badge && (
        <div className="absolute -top-2 -right-2 z-10 px-3 py-1 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg animate-bounce">
          {badge}
        </div>
      )}
      <div className="h-full flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-white to-zinc-50 p-6 shadow-md hover:shadow-2xl transition-all duration-300 border border-zinc-100 group-hover:scale-105 group-hover:-translate-y-1">
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            <div
              className={`absolute inset-0 bg-gradient-to-br ${color} rounded-xl blur-md opacity-0 group-hover:opacity-75 transition-opacity duration-300`}
            ></div>
            <IconBadge color={color}>
              <Icon className="h-6 w-6 text-white group-hover:scale-110 transition-transform duration-300" />
            </IconBadge>
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-bold text-zinc-900 mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-zinc-900 group-hover:to-zinc-600 transition-all">
              {title}
            </h4>
            <p className="text-sm leading-relaxed text-zinc-600">{desc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/60 backdrop-blur border border-white/40 hover:bg-white/80 transition-all duration-300 hover:scale-105">
      <Icon className="h-6 w-6 text-[#4CAF50] flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-zinc-900 mb-1">{title}</h4>
        <p className="text-sm text-zinc-600">{desc}</p>
      </div>
    </div>
  );
}

function IconBadge({ children, color = BRAND.primary }) {
  return (
    <span
      className={`relative inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${color} shadow-lg`}
    >
      {children}
    </span>
  );
}
